import React, { useEffect, useMemo, useRef, useState } from 'react';

// ⌘K search across every action — open and completed — ranked by where the
// match lives: title > outcome > next step > checklist > note body. The whole
// workspace is already in memory, so this is a per-keystroke filter, no index.

function stripMarkdown(md) {
  return (md || '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')      // images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')    // links → text
    .replace(/[#>*`_~\-|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function findSnippet(text, terms) {
  const lower = text.toLowerCase();
  let pos = -1;
  for (const t of terms) {
    const i = lower.indexOf(t);
    if (i !== -1 && (pos === -1 || i < pos)) pos = i;
  }
  if (pos === -1) return null;
  const start = Math.max(0, pos - 40);
  const end = Math.min(text.length, pos + 60);
  return (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
}

// Render text with query terms highlighted
function Highlight({ text, terms }) {
  if (!text) return null;
  const pattern = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  if (!pattern) return <>{text}</>;
  const parts = text.split(new RegExp(`(${pattern})`, 'ig'));
  return (
    <>
      {parts.map((p, i) =>
        terms.includes(p.toLowerCase())
          ? <mark key={i} style={{ background: 'color-mix(in srgb, var(--color-primary) 22%, transparent)', color: 'inherit', borderRadius: '2px', padding: 0 }}>{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

export default function SearchPalette({ open, onClose, actions, completedActions, archivedActions = [], projects, goals, onOpenAction }) {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setIndex(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const results = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];
    const all = [
      ...actions.map(g => ({ ...g, _done: false })),
      ...completedActions.map(g => ({ ...g, _done: true })),
      // Archived work stays part of memory — search must never say
      // "nothing matches" about things you did.
      ...archivedActions.map(g => ({ ...g, _done: true, _archived: true })),
    ];
    const scored = [];
    for (const g of all) {
      const title = (g.text || '').toLowerCase();
      const body = stripMarkdown(g.notes).toLowerCase();
      const next = (g.nextStep || '').toLowerCase();
      const outcome = (g.resolutionComment || '').toLowerCase();
      const checklist = (g.checklist || []).map(a => a.text || '').join(' ').toLowerCase();
      let score = 0;
      let allMatch = true;
      for (const t of terms) {
        let s = 0;
        if (title.startsWith(t)) s = 100;
        else if (title.includes(t)) s = 70;
        if (outcome.includes(t)) s = Math.max(s, 45);
        if (next.includes(t)) s = Math.max(s, 40);
        if (checklist.includes(t)) s = Math.max(s, 30);
        if (body.includes(t)) s = Math.max(s, 35);
        if (s === 0) { allMatch = false; break; }
        score += s;
      }
      if (!allMatch) continue;
      if (g._done) score -= 15;      // live work slightly outranks finished work
      if (g._archived) score -= 10;  // …and finished outranks shelved
      const bodyPlain = stripMarkdown(g.notes);
      scored.push({
        action: g,
        score,
        snippet: title.includes(terms[0]) ? null
          : findSnippet(bodyPlain, terms) || findSnippet(g.nextStep || '', terms) || findSnippet(g.resolutionComment || '', terms),
      });
    }
    scored.sort((a, b) => b.score - a.score || (new Date(b.action.lastTouchedAt || b.action.createdDate || 0)) - (new Date(a.action.lastTouchedAt || a.action.createdDate || 0)));
    return scored.slice(0, 30);
  }, [query, actions, completedActions, archivedActions]);

  useEffect(() => { setIndex(0); }, [query]);

  // Keep the active row in view
  useEffect(() => {
    listRef.current?.children[index]?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  if (!open) return null;

  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const crumbFor = (g) => {
    const proj = g.linkType === 'project' ? projects.find(p => p.id === g.linkId) : null;
    const obj = proj ? goals.find(i => i.id === proj.goalId) : null;
    return proj ? (obj ? `${obj.name} › ${proj.name}` : proj.name) : 'No project';
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndex(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[index]) { e.preventDefault(); onOpenAction(results[index].action.id); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center"
      style={{ background: 'rgba(0,0,0,0.28)', paddingTop: '14vh' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-[600px] max-w-[92vw] rounded-xl shadow-2xl border themed-border themed-background overflow-hidden search-palette-in"
        onKeyDown={onKeyDown}
      >
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search every note — titles, text, next steps, outcomes…"
          className="w-full px-4 py-3.5 text-[15px] themed-text bg-transparent focus:outline-none border-b themed-border-light placeholder:opacity-50"
        />
        {query && (
          <div ref={listRef} className="max-h-[46vh] overflow-y-auto py-1">
            {results.length === 0 && (
              <p className="px-4 py-6 text-sm themed-text-muted text-center">Nothing matches “{query}”</p>
            )}
            {results.map((r, i) => (
              <button
                key={`${r.action.id}-${r.action._done}`}
                onClick={() => onOpenAction(r.action.id)}
                onMouseEnter={() => setIndex(i)}
                className="w-full text-left px-4 py-2.5 flex flex-col gap-0.5 transition-colors"
                style={i === index ? { background: 'var(--color-surfaceHover)' } : {}}
              >
                <span className={`text-[13.5px] font-medium themed-text leading-snug ${r.action._done ? 'opacity-60' : ''}`}>
                  <Highlight text={r.action.text} terms={terms} />
                  {r.action._archived
                    ? <span className="ml-2 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">Archived</span>
                    : r.action._done && <span className="ml-2 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Done</span>}
                </span>
                <span className="text-[10.5px] themed-text-muted">{crumbFor(r.action)}</span>
                {r.snippet && (
                  <span className="text-[11.5px] themed-text-muted leading-snug">
                    <Highlight text={r.snippet} terms={terms} />
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <div className="px-4 py-2 border-t themed-border-light flex items-center gap-3 text-[10px] themed-text-muted">
          <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
          {query && results.length > 0 && <span className="ml-auto">{results.length} result{results.length === 1 ? '' : 's'}</span>}
        </div>
      </div>
    </div>
  );
}
